"use client";

import { useEffect, useState } from "react";
import {
  getAuth as getSecondaryAuth,
  createUserWithEmailAndPassword,
} from "firebase/auth";
import { ref, update, get, remove, onValue ,set} from "firebase/database";
import { auth, db, firebaseConfig } from "../Firebase/firebase-config";
import { initializeApp } from "firebase/app";
import "./PartnerManagement.css";
import { FaUserPlus, FaTrash, FaInfoCircle, FaCheck, FaTimes } from "react-icons/fa";

const secondaryApp = initializeApp(firebaseConfig, "Secondary");
const secondaryAuth = getSecondaryAuth(secondaryApp);

export default function PartnerManagement() {
  const [firms, setFirms] = useState([]);
  const [partners, setPartners] = useState([]);
  const [newPartner, setNewPartner] = useState({ name: "", email: "", password: "", phone: "" });
  const [selectedFirms, setSelectedFirms] = useState({});
  const [message, setMessage] = useState({ text: "", type: "" });
  const [phoneError, setPhoneError] = useState("");
  const [nameError, setNameError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordStrength, setPasswordStrength] = useState("");
  const [onlinePartners, setOnlinePartners] = useState({});
  const [confirmAction, setConfirmAction] = useState(null);

  const mainUserId = auth.currentUser?.uid;

  // Fetch firms & partners
  useEffect(() => {
    if (!mainUserId) return;

    const fetchData = async () => {
      try {
        // Fetch Firms
        const firmsSnap = await get(ref(db, `profileDashboard/${mainUserId}/firms`));
        const firmsData = firmsSnap.val() || {};
        const firmsList = Object.entries(firmsData).map(([id, data]) => ({
          id,
          company_name: data.company_name || "Unnamed Firm",
          gstin: data.gstin || "N/A",
        }));
        setFirms(firmsList);

        // Fetch Partner UIDs (only true flags)
        const partnersSnap = await get(ref(db, `profileDashboard/${mainUserId}/partners`));
        const partnerUids = partnersSnap.exists() 
          ? Object.keys(partnersSnap.val()).filter(uid => partnersSnap.val()[uid] === true)
          : [];

        // Fetch full partner data from their own profile
        const partnersData = [];
        for (const uid of partnerUids) {
          const partnerSnap = await get(ref(db, `profileDashboard/${uid}`));
          if (partnerSnap.exists()) {
            partnersData.push({
              uid,
              ...partnerSnap.val(),
              assignedFirms: partnerSnap.val().assignedFirms || [],
            });
          }
        }

        setPartners(partnersData);
      } catch (err) {
        console.error(err);
        showMessage("Failed to load data", "error");
      }
    };

    fetchData();
  }, [mainUserId]);

  // Password Strength & Validation
  const getStrength = (pwd) => {
    let strength = 0;
    if (pwd.length >= 8) strength++;
    if (/[a-z]/.test(pwd)) strength++;
    if (/[A-Z]/.test(pwd)) strength++;
    if (/\d/.test(pwd)) strength++;
    if (/[^A-Za-z0-9]/.test(pwd)) strength++;
    return strength;
  };

  const validatePassword = (value) => {
    if (!value) {
      setPasswordError("Password is required");
      setPasswordStrength("");
      return false;
    }
    if (value.length < 8) {
      setPasswordError("Password must be at least 8 characters");
      setPasswordStrength("");
      return false;
    }
    if (!/[a-z]/.test(value)) {
      setPasswordError("Must contain at least one lowercase letter");
      setPasswordStrength("");
      return false;
    }
    if (!/[A-Z]/.test(value)) {
      setPasswordError("Must contain at least one uppercase letter");
      setPasswordStrength("");
      return false;
    }
    if (!/\d/.test(value)) {
      setPasswordError("Must contain at least one number");
      setPasswordStrength("");
      return false;
    }
    if (!/[^A-Za-z0-9]/.test(value)) {
      setPasswordError("Must contain at least one special character");
      setPasswordStrength("");
      return false;
    }

    setPasswordError("");
    const strengthLevel = getStrength(value);
    if (strengthLevel === 5) setPasswordStrength("Strong ✅");
    else if (strengthLevel >= 4) setPasswordStrength("Good");
    else setPasswordStrength("Fair");
    return true;
  };

  const handlePasswordChange = (e) => {
    const val = e.target.value;
    setNewPartner({ ...newPartner, password: val });
    validatePassword(val);
  };

  // Phone & Name Validation (unchanged)
  const validatePhone = (value) => {
    const digitsOnly = value.replace(/\D/g, "");
    if (!digitsOnly) {
      setPhoneError("Phone number is required");
      return false;
    }
    if (digitsOnly.length < 10) {
      setPhoneError("Phone number must be at least 10 digits");
      return false;
    }
    if (digitsOnly.length > 15) {
      setPhoneError("Phone number cannot exceed 15 digits");
      return false;
    }
    setPhoneError("");
    return true;
  };

  const validateName = (value) => {
    if (!value.trim()) {
      setNameError("Partner name is required");
      return false;
    }
    if (value.trim().length > 50) {
      setNameError("Partner name cannot exceed 50 characters");
      return false;
    }
    setNameError("");
    return true;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    const sanitized = value.replace(/[^0-9+]/g, "");
    setNewPartner({ ...newPartner, phone: sanitized });
    validatePhone(sanitized);
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    setNewPartner({ ...newPartner, name: value });
    validateName(value);
  };

  // Online Status Listener
  useEffect(() => {
    const onlineRef = ref(db, "onlineStatus/partners");
    const unsubscribe = onValue(onlineRef, (snapshot) => {
      const data = snapshot.val() || {};
      const now = Date.now();
      const statusMap = {};

      Object.entries(data).forEach(([uid, value]) => {
        if (value === "online") {
          statusMap[uid] = { online: true, label: "Active now" };
          return;
        }
        if (typeof value !== "number") {
          statusMap[uid] = { online: false, label: "Offline" };
          return;
        }

        const diffMs = now - value;
        const diffMin = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMin / 60);

        if (diffMin < 2) statusMap[uid] = { online: true, label: "Active now" };
        else if (diffMin < 60) statusMap[uid] = { online: false, label: `${diffMin} min ago` };
        else if (diffHours < 24) statusMap[uid] = { online: false, label: `${diffHours} h ago` };
        else {
          const lastSeenDate = new Date(value);
          const today = new Date(now);
          const yesterday = new Date(now - 86400000);

          let datePart = lastSeenDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
          if (lastSeenDate.toDateString() === today.toDateString()) datePart = "Today";
          else if (lastSeenDate.toDateString() === yesterday.toDateString()) datePart = "Yesterday";

          const timeStr = lastSeenDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });

          statusMap[uid] = { online: false, label: `${datePart} at ${timeStr}` };
        }
      });

      setOnlinePartners(statusMap);
    });

    return () => unsubscribe();
  }, []);

  const showMessage = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 5000);
  };

  // ====================== ADD PARTNER ======================
  const handleAddPartner = async (e) => {
    e.preventDefault();

    const isNameValid = validateName(newPartner.name);
    const isPhoneValid = validatePhone(newPartner.phone);
    const isPasswordValid = validatePassword(newPartner.password);

    if (!isNameValid || !isPhoneValid || !isPasswordValid || !newPartner.email.trim()) {
      showMessage("Please fix the errors above", "error");
      return;
    }

    const selectedIds = Object.keys(selectedFirms).filter((id) => selectedFirms[id]);

    setConfirmAction({
      type: "add",
      data: { ...newPartner, firmIds: selectedIds },
    });
  };

 const confirmAddPartner = async () => {
  const { name, email, password, phone, firmIds } = confirmAction.data;

  try {
    console.log("Attempting to create partner with email:", email.trim()); // ← Debug

    const cred = await createUserWithEmailAndPassword(
      secondaryAuth, 
      email.trim(), 
      password
    );

    const uid = cred.user.uid;
    console.log("Partner created successfully. UID:", uid); // ← Debug

    const partnerData = {
      uid,
      name: name.trim(),
      email: email.trim(),
      phone: phone.replace(/\D/g, ""),
      assignedFirms: firmIds || [],
      role: "viewer",
      type: "partner",
      ownerId: mainUserId,
      createdAt: Date.now(),
    };

    // 1. Store only reference under main user
    await update(ref(db, `profileDashboard/${mainUserId}/partners`), {
  [uid]: true
});

    // 2. Store full data under partner profile
    await update(ref(db, `profileDashboard/${uid}`), partnerData);

    // Update UI immediately
    setPartners(prev => [...prev, { ...partnerData, assignedFirms: firmIds || [] }]);

    showMessage("Partner added successfully!", "success");

    // Reset form
    setNewPartner({ name: "", email: "", password: "", phone: "" });
    setSelectedFirms({});
    setNameError(""); 
    setPhoneError(""); 
    setPasswordError(""); 
    setPasswordStrength("");

  } catch (err) {
    console.error("Partner creation error:", err.code, err.message); // ← Very important for debugging

    let errorMsg = "Failed to create partner account";

    switch (err.code) {
      case "auth/email-already-in-use":
        errorMsg = "This email is already in use by another account";
        break;
      case "auth/invalid-email":
        errorMsg = "Invalid email address format";
        break;
      case "auth/weak-password":
        errorMsg = "Password is too weak. Please use a stronger password";
        break;
      case "auth/operation-not-allowed":
        errorMsg = "Email/Password sign-up is not enabled in Firebase Console";
        break;
      case "auth/network-request-failed":
        errorMsg = "Network error. Please check your internet connection";
        break;
      default:
        errorMsg = err.message || "Unknown error occurred";
    }

    showMessage(errorMsg, "error");
  } finally {
    setConfirmAction(null);
  }
};
  // ====================== TOGGLE FIRM ACCESS ======================
  const toggleFirmAccess = (partnerUid, firmId) => {
    const partner = partners.find((p) => p.uid === partnerUid);
    if (!partner) return;

    const hasAccess = partner.assignedFirms.includes(firmId);
    setConfirmAction({
      type: "toggleFirm",
      data: { partnerUid, firmId, hasAccess, partnerName: partner.name },
    });
  };

  const confirmToggleFirm = async () => {
  const { partnerUid, firmId, hasAccess } = confirmAction.data;
  const partner = partners.find((p) => p.uid === partnerUid);
  if (!partner) return;

  const newAssigned = hasAccess
    ? partner.assignedFirms.filter((id) => id !== firmId)
    : [...partner.assignedFirms, firmId];

  // Keep reference
  await set(
    ref(db, `profileDashboard/${mainUserId}/partners/${partnerUid}`),
    true
  );

  // Update assigned firms (FIXED)
  await set(
    ref(db, `profileDashboard/${partnerUid}/assignedFirms`),
    newAssigned
  );

  setPartners((prev) =>
    prev.map((p) =>
      p.uid === partnerUid ? { ...p, assignedFirms: newAssigned } : p
    )
  );

  showMessage(hasAccess ? "Firm access revoked" : "Firm access granted!", "success");
  setConfirmAction(null);
};
  // ====================== REMOVE PARTNER ======================
  const confirmRemovePartner = async () => {
    const uid = confirmAction.data.uid;

    try {
      await remove(ref(db, `profileDashboard/${mainUserId}/partners/${uid}`));
      await remove(ref(db, `profileDashboard/${uid}`));
      await remove(ref(db, `userchatbox/${uid}`));
      await remove(ref(db, `guestchatbox/${uid}`));
      await remove(ref(db, `onlineStatus/partners/${uid}`));

      // Remove from group chats...
      const groupSnap = await get(ref(db, "groupChats"));
      const groupData = groupSnap.val() || {};
      const updates = {};

      Object.entries(groupData).forEach(([groupId, group]) => {
        if (group.members?.includes(uid)) {
          updates[`groupChats/${groupId}/members`] = group.members.filter(m => m !== uid);
          if (group.messages) {
            Object.keys(group.messages).forEach(msgId => {
              if (group.messages[msgId].sender === uid) {
                updates[`groupChats/${groupId}/messages/${msgId}`] = null;
              }
            });
          }
        }
      });

      if (Object.keys(updates).length > 0) {
        await update(ref(db), updates);
      }

      setPartners((prev) => prev.filter((p) => p.uid !== uid));
      showMessage("Partner removed successfully", "success");

    } catch (err) {
      console.error(err);
      showMessage("Failed to remove partner", "error");
    } finally {
      setConfirmAction(null);
    }
  };

 const partnerInfo = () => {
  alert(`
HOW TO MANAGE PARTNER FIRMS (Simple Steps)

ADDING FIRMS:
1. Enter Partner Name (e.g. Rahul CA)
2. Enter their Email (e.g. rahul@ca.com)
3. Set a Password for them
4. Tick the firms they should see
5. Click "Create Partner Account"

REMOVING FIRMS:
1. Find the partner in "Your Partners" list
2. Scroll to "Accessible Firms"
3. Click on the firm you want to remove (it will turn gray)
4. Changes save automatically!

Partners can only VIEW Order Status, downloads MTC , Statements and update Stock for assigned firms.
`.trim());
};


  return (
    <div className="pm-container">
      <div className="pm-grid">
        {/* Add Partner Card */}
        <div className="pm-card">
          <div className="pm-card-header">
            <h2>
              <FaUserPlus className="pm-icon" /> Add New Partner
              <FaInfoCircle
                className="pm-info-icon"
                title="Create partner account and assign accessible firms"
                onClick={partnerInfo}
              />
            </h2>
          </div>

          <form onSubmit={handleAddPartner} className="pm-form">
           <input
              type="text"
              placeholder="Partner Name (max 50 characters)"
              value={newPartner.name}
              onChange={handleNameChange}
              className="pm-input"
              maxLength={50}
            />
            {nameError && <p className="perror-text">{nameError}</p>}
            <input
              type="email"
              placeholder="partner@example.com"
              value={newPartner.email}
              onChange={(e) => setNewPartner({ ...newPartner, email: e.target.value })}
              className="pm-input"
              autoComplete="off"
            />
            <input
              type="tel"
              placeholder="Phone Number (10 to 15 digits)"
              value={newPartner.phone}
              onChange={handlePhoneChange}
              className="pm-input"
            />
            {phoneError && <p className="perror-text">{phoneError}</p>}
            <input
              type="password"
              placeholder="Password (min 8 chars, strong)"
              value={newPartner.password}
              onChange={handlePasswordChange}
              className="pm-input"
              autoComplete="new-password"
              required
            />
            <div style={{ position: "relative" }}>
              {passwordError && <p style={{ color: "red", fontSize: "0.85rem", marginTop: "4px" }}>{passwordError}</p>}
              {!passwordError && passwordStrength && (
                <p style={{
                  color: passwordStrength.includes("Strong") ? "green" : passwordStrength.includes("Good") ? "orange" : "gray",
                  fontSize: "0.85rem",
                  marginTop: "4px"
                }}>
                  Password strength: {passwordStrength}
                </p>
              )}
            </div>


            <div className="pm-firm-list">
              <label className="pm-label">Assign Firms (Optional)</label>
              {firms.length === 0 ? (
                <p className="pm-empty-text">No firms available</p>
              ) : (
                firms.map((firm) => (
                  <label key={firm.id} className="pm-checkbox-label">
                    <input
                      type="checkbox"
                      checked={!!selectedFirms[firm.id]}
                      onChange={(e) =>
                        setSelectedFirms({ ...selectedFirms, [firm.id]: e.target.checked })
                      }
                    />
                    <span>
                      <strong>{firm.company_name}</strong>
                    </span>
                  </label>
                ))
              )}
            </div>

            <button type="submit" className="pm-btn pm-btn-primary">
              Create Partner Account
            </button>
          </form>
        </div>

        {/* Partners List */}
        <div className="pm-card pm-partners-card">
          <div className="pm-card-header">
            <h2>Your Partners ({partners.length})</h2>
            <p className="pm-subtitle">Click on a firm to grant/revoke access</p>
          </div>

          <div className="pm-partners-list">
            {partners.length === 0 ? (
  <div className="pm-empty">
    <p>No partners added yet</p>
  </div>
) : (
  partners.map((partner) => {
    const status = onlinePartners[partner.uid] || { online: false, label: "Offline" };

    return (
      <div key={partner.uid} className="pm-partner-item">
        {/* Header: Name, Email, Delete */}
        <div className="pm-partner-header">
          <div>
            <h3>{partner.name}</h3>
            <p>{partner.email}</p>
            <span className="pm-role">Viewer</span>
          </div>
          <button
            onClick={() => setConfirmAction({ type: "removePartner", data: { uid: partner.uid } })}
            className="pm-btn pm-btn-danger-sm"
          >
            <FaTrash />
          </button>
        </div>

        {/* Online Status — Clean and Prominent */}
        <div className="partner-status">
          <span className={`status-dot ${status.online ? "online" : "offline"}`}></span>
          <span className="status-text">{status.label}</span>
        </div>

        {/* Accessible Firms */}
        <div className="pm-firm-access">
          <strong>Accessible Firms:</strong>
          <div className="pm-firm-grid">
            {firms.map((firm) => {
              const hasAccess = partner.assignedFirms.includes(firm.id);
              return (
                <div
                  key={firm.id}
                  className={`pm-firm-tag ${hasAccess ? "active" : ""}`}
                  onClick={() => toggleFirmAccess(partner.uid, firm.id)}
                >
                  {hasAccess && <FaCheck className="pm-check" />}
                  {!hasAccess && <FaTimes className="pm-cross" />}
                  <span>{firm.company_name}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  })
)}
          </div>
        </div>
      </div>

      {/* Global Message */}
      {message.text && (
        <div className={`pm-toast ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmAction && (
        <div className="pm-modal-overlay">
          <div className="pm-modal">
            <h3>
              {confirmAction.type === "add" && "Create Partner Account?"}
              {confirmAction.type === "removePartner" && "Remove Partner?"}
              {confirmAction.type === "toggleFirm" &&
                (confirmAction.data.hasAccess
                  ? "Revoke Firm Access?"
                  : "Grant Firm Access?")}
            </h3>
            <p>
              {confirmAction.type === "add" &&
    `Add "${confirmAction.data.name}" with access to ${
      confirmAction.data.firmIds.length === 0
        ? "no firms (you can assign later)"
        : confirmAction.data.firmIds.length + " firm(s)"
    }?`}
              {confirmAction.type === "removePartner" && "This action cannot be undone."}
              {confirmAction.type === "toggleFirm" &&
                `${confirmAction.data.hasAccess ? "Remove" : "Grant"} access to "${firms.find((f) => f.id === confirmAction.data.firmId)?.company_name
                }" for ${confirmAction.data.partnerName}?`}
            </p>
            <div className="pm-modal-actions">
              <button
                onClick={() => setConfirmAction(null)}
                className="pm-btn pm-btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={
                  confirmAction.type === "add"
                    ? confirmAddPartner
                    : confirmAction.type === "removePartner"
                      ? confirmRemovePartner
                      : confirmToggleFirm
                }
                className={`pm-btn ${confirmAction.type === "removePartner" ? "pm-btn-danger" : "pm-btn-primary"
                  }`}
              >
                {confirmAction.type === "toggleFirm"
                  ? confirmAction.data.hasAccess
                    ? "Revoke"
                    : "Grant"
                  : confirmAction.type === "removePartner"
                    ? "Remove"
                    : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}